import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Heading3,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { apiClient } from '../../utils/api';

export default function RichTextEditor({ content, onChange, onMediaSelect }) {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Започнете да пишете...',
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-4 py-3',
      },
    },
  });

  const loadMediaLibrary = async () => {
    try {
      setLoadingMedia(true);
      const response = await apiClient.get('/api/media/?type=image&limit=100');
      setMediaLibrary(response);
    } catch (error) {
      console.error('Failed to load media:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleInsertLink = () => {
    if (linkUrl && editor) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkDialog(false);
    }
  };

  const handleInsertImage = (media) => {
    if (editor) {
      editor.chain().focus().setImage({ 
        src: media.url_medium || media.url_original,
        alt: media.alt_text || media.title 
      }).run();
      setShowMediaPicker(false);
      if (onMediaSelect) {
        onMediaSelect(media);
      }
    }
  };

  const handleOpenMediaPicker = () => {
    setShowMediaPicker(true);
    loadMediaLibrary();
  };

  if (!editor) {
    return null;
  }

  const MenuButton = ({ onClick, active, children, title }) => (
    <button
      onClick={onClick}
      type="button"
      className={`p-2 rounded hover:bg-neutral-700 transition-colors ${
        active ? 'bg-neutral-700 text-accent-gold' : 'text-neutral-300'
      }`}
      title={title}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-neutral-700 rounded-lg overflow-hidden bg-primary-charcoal">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-neutral-700 bg-primary-black">
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </MenuButton>

        <div className="w-px h-6 bg-neutral-700 mx-1" />

        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </MenuButton>

        <div className="w-px h-6 bg-neutral-700 mx-1" />

        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Ordered List"
        >
          <ListOrdered className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </MenuButton>

        <div className="w-px h-6 bg-neutral-700 mx-1" />

        <MenuButton onClick={() => setShowLinkDialog(true)} title="Insert Link">
          <LinkIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton onClick={handleOpenMediaPicker} title="Insert Image">
          <ImageIcon className="w-4 h-4" />
        </MenuButton>

        <div className="w-px h-6 bg-neutral-700 mx-1" />

        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
        >
          <Undo className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
        >
          <Redo className="w-4 h-4" />
        </MenuButton>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="bg-primary-charcoal border-neutral-700">
          <DialogHeader>
            <DialogTitle>Вмъкване на линк</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInsertLink()}
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLinkDialog(false)}
                className="flex-1"
              >
                Откажи
              </Button>
              <Button
                onClick={handleInsertLink}
                className="flex-1 bg-accent-gold hover:bg-accent-gold-dark"
              >
                Вмъкни
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="bg-primary-charcoal border-neutral-700 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Избор на изображение</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {loadingMedia ? (
              <div className="text-center py-10">Зареждане...</div>
            ) : mediaLibrary.length === 0 ? (
              <div className="text-center py-10 text-neutral-400">
                Няма налични изображения
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {mediaLibrary.map((media) => (
                  <div
                    key={media._id}
                    onClick={() => handleInsertImage(media)}
                    className="aspect-square bg-primary-black rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-accent-gold transition-all"
                  >
                    <img
                      src={media.url_thumbnail || media.url_small || media.url_original}
                      alt={media.alt_text || media.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
